import type { ComponentType } from 'react';

/** Module lifecycle — only `approved` modules render in the main `/` flow. */
export type ModuleStatus = 'draft' | 'review' | 'approved';

/** Meta exported by every module's `index.tsx`. */
export interface ModuleMeta {
  id: string;
  title: string;
  status: ModuleStatus;
  /** Display viewport in `<w>x<h>` form, e.g. `390x844`. Informational only. */
  viewport: string;
}

export interface RegisteredModule extends ModuleMeta {
  Component: ComponentType;
}

type ModuleExports = {
  default: ComponentType;
  meta?: ModuleMeta;
};

/**
 * Eager glob — modules are small leaf trees; adding a module = creating a file,
 * never editing a list. Folders starting with `_` (`_nav`, `_shared`) are skipped:
 * they hold supporting components without `meta`.
 */
const rawModules = import.meta.glob<ModuleExports>('../modules/*/index.tsx', {
  eager: true,
});

const registry = new Map<string, RegisteredModule>();

for (const [path, mod] of Object.entries(rawModules)) {
  if (path.includes('/_')) continue;
  if (!mod.meta) {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.warn(`[ModuleRegistry] missing meta export for ${path}`);
    }
    continue;
  }
  registry.set(mod.meta.id, { ...mod.meta, Component: mod.default });
}

export function listModules(): RegisteredModule[] {
  return Array.from(registry.values()).sort((a, b) => a.id.localeCompare(b.id));
}

export function getModule(id: string): RegisteredModule | undefined {
  return registry.get(id);
}
